import React, { useState, useEffect, useRef } from 'react'
import { auth, db } from '../firebase'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getBrandImage } from '../data/brandImages'
import { normalizarTelefono } from '../utils/phoneUtils'
import { trackEvento, EVENTOS } from '../utils/analytics'

const iniciarOnboardingFn  = httpsCallable(getFunctions(undefined, 'us-central1'), 'iniciarOnboarding')
const generarTokenWAFn     = httpsCallable(getFunctions(undefined, 'us-central1'), 'generarTokenWA')

const PASO = {
  FORM:     'form',      // Completar datos personales
  VALIDAR:  'validar',   // Botón "Validar mi WhatsApp"
  ESPERA:   'espera',    // Esperando confirmación desde WhatsApp
  COMPLETO: 'completo',  // onboardingCompletado === true
}

const TTL_MS = 15 * 60 * 1000

/**
 * Flujo unificado de onboarding para Google y email/password.
 *
 * Props:
 *   googleUser   — objeto Firebase Auth (nulo si viene de email/password)
 *   siteConfig   — configuración del sitio
 *   onComplete   — callback cuando el onboarding termina exitosamente
 */
const OnboardingFlow = ({ googleUser, siteConfig, onComplete }) => {
  const logo = getBrandImage(siteConfig, 'logo')

  // Datos pre-llenados desde Google si aplica
  const [form, setForm] = useState({
    nombre:     googleUser?.displayName?.split(' ')[0] || '',
    apellido:   googleUser?.displayName?.split(' ').slice(1).join(' ') || '',
    telefono:   '',
    dni:        '',
    domicilio:  '',
  })
  const [nacDia,  setNacDia]  = useState('')
  const [nacMes,  setNacMes]  = useState('')
  const [nacAnio, setNacAnio] = useState('')
  const nacDiaRef  = useRef(null)
  const nacMesRef  = useRef(null)
  const nacAnioRef = useRef(null)
  const [paso, setPaso]               = useState(PASO.FORM)
  const [error, setError]             = useState('')
  const [guardando, setGuardando]     = useState(false)
  const [generando, setGenerando]     = useState(false)
  const [waUrl, setWaUrl]             = useState('')
  const [expiresAt, setExpiresAt]     = useState(null)
  const [tiempoRestante, setTiempoRestante] = useState(null)
  const [cuentaExistente, setCuentaExistente] = useState(false)
  const timerRef    = useRef(null)
  const snapshotRef = useRef(null)

  // Detectar perfil ya existente (caso: registro email/password que ya llamó iniciarOnboarding)
  useEffect(() => {
    const checkExistingProfile = async () => {
      const user = auth.currentUser
      if (!user) return
      try {
        const snap = await getDoc(doc(db, 'usuarios', user.uid))
        if (snap.exists()) {
          const data = snap.data()
          if (data.onboardingCompletado === true) { onComplete(); return }
          // Perfil existe pero onboarding pendiente → rellenar form y saltar a VALIDAR
          setForm({
            nombre:    data.nombre    || '',
            apellido:  data.apellido  || '',
            telefono:  data.telefono  || '',
            dni:       data.dni       || '',
            domicilio: data.domicilio || '',
          })
          if (data.nacimiento) {
            const [ay, am, ad] = data.nacimiento.split('-')
            if (ay) setNacAnio(ay)
            if (am) setNacMes(String(parseInt(am, 10)))
            if (ad) setNacDia(String(parseInt(ad, 10)))
          }
          setPaso(PASO.VALIDAR)
        }
      } catch {}
    }
    checkExistingProfile()
    trackEvento(EVENTOS.ONBOARDING_STARTED || 'onboarding_started')
  }, [])

  // Limpieza de timers y listeners al desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (snapshotRef.current) snapshotRef.current()
    }
  }, [])

  // ─── Countdown cuando está en paso ESPERA ──────────────────────────────────
  useEffect(() => {
    if (paso !== PASO.ESPERA || !expiresAt) return
    if (timerRef.current) clearInterval(timerRef.current)

    const tick = () => {
      const restante = expiresAt - Date.now()
      if (restante <= 0) {
        setTiempoRestante(0)
        clearInterval(timerRef.current)
      } else {
        setTiempoRestante(Math.ceil(restante / 1000))
      }
    }
    tick()
    timerRef.current = setInterval(tick, 1000)
    return () => clearInterval(timerRef.current)
  }, [paso, expiresAt])

  const updateForm = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }))
    setError('')
  }

  // ─── PASO 1: Guardar perfil con iniciarOnboarding ─────────────────────────
  const handleGuardarPerfil = async (e) => {
    e.preventDefault()
    setError('')
    const { nombre, apellido, telefono, dni } = form
    if (!nombre.trim())    { setError('El nombre es requerido.'); return }
    if (!telefono.trim())  { setError('El teléfono es requerido.'); return }
    if (!dni.trim())       { setError('El DNI es requerido.'); return }

    // Componer y validar fecha de nacimiento
    let nacimientoFinal = ''
    if (nacDia || nacMes || nacAnio) {
      const d = parseInt(nacDia,  10)
      const m = parseInt(nacMes,  10)
      const y = parseInt(nacAnio, 10)
      const fechaOk = d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= new Date().getFullYear()
      if (!fechaOk) { setError('La fecha de nacimiento no es válida.'); return }
      const fecha = new Date(y, m - 1, d)
      if (fecha.getFullYear() !== y || fecha.getMonth() !== m - 1 || fecha.getDate() !== d) {
        setError('La fecha de nacimiento no es válida.'); return
      }
      nacimientoFinal = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`
    }

    setGuardando(true)
    try {
      const user = auth.currentUser
      if (!user) throw new Error('Sesión perdida')

      await iniciarOnboardingFn({
        nombre:     nombre.trim(),
        apellido:   (apellido || '').trim(),
        telefono:   normalizarTelefono(telefono.trim()),
        dni:        dni.trim(),
        nacimiento: nacimientoFinal,
        domicilio:  form.domicilio || '',
      })

      trackEvento(EVENTOS.ONBOARDING_PROFILE_SAVED || 'onboarding_profile_saved')
      setPaso(PASO.VALIDAR)
    } catch (err) {
      console.error('[Onboarding] iniciarOnboarding error:', err)
      if (err?.message === 'CLUB_MANTENIMIENTO') {
        setError('El Club está en mantenimiento. Intentá de nuevo en unos minutos.')
      } else {
        setError('No se pudo guardar tu perfil. Intentá de nuevo.')
      }
    } finally {
      setGuardando(false)
    }
  }

  // ─── PASO 2: Generar token y abrir WhatsApp ───────────────────────────────
  const handleValidarWhatsApp = async () => {
    setError('')
    setGenerando(true)
    try {
      const result = await generarTokenWAFn()
      const data   = result.data

      if (data.yaVerificado) {
        // Ya estaba verificado — completar el flujo
        onComplete()
        return
      }

      setWaUrl(data.waUrl)
      setExpiresAt(data.expiresAt)
      setPaso(PASO.ESPERA)

      // Abrir WhatsApp inmediatamente
      window.open(data.waUrl, '_blank')

      // Listener en tiempo real para detectar onboardingCompletado === true
      iniciarListenerOnboarding()

      trackEvento(EVENTOS.WHATSAPP_VALIDATION_OPENED || 'wa_validation_opened')
    } catch (err) {
      console.error('[Onboarding] generarTokenWA error:', err)
      if (err?.code === 'functions/resource-exhausted') {
        setError('Generaste demasiados códigos seguidos. Esperá unos minutos e intentá de nuevo.')
      } else if (err?.message === 'wa_number_not_configured') {
        setError('No está configurado el número de WhatsApp del Club. Contactá al administrador.')
      } else {
        setError('No se pudo generar el código de verificación. Intentá de nuevo.')
      }
    } finally {
      setGenerando(false)
    }
  }

  // ─── Listener Firestore: detecta cuando el webhook completa el onboarding ─
  const iniciarListenerOnboarding = () => {
    if (snapshotRef.current) snapshotRef.current()
    const user = auth.currentUser
    if (!user) return

    snapshotRef.current = onSnapshot(
      doc(db, 'usuarios', user.uid),
      (snap) => {
        if (!snap.exists()) return
        const data = snap.data()

        if (data.onboardingCompletado === true) {
          if (snapshotRef.current) { snapshotRef.current(); snapshotRef.current = null }
          trackEvento(EVENTOS.ONBOARDING_COMPLETED || 'onboarding_completed')
          onComplete()
          return
        }

        // El handler detectó cuenta duplicada → mostrar estado específico
        if (data.validacionWAEstado === 'CUENTA_EXISTENTE') {
          if (snapshotRef.current) { snapshotRef.current(); snapshotRef.current = null }
          if (timerRef.current) clearInterval(timerRef.current)
          setCuentaExistente(true)
        }
      },
      (err) => {
        console.warn('[Onboarding] listener error:', err.message)
      }
    )
  }

  // ─── Reabrir WhatsApp si el usuario volvió sin completar ─────────────────
  const handleReopenWA = () => {
    if (waUrl) window.open(waUrl, '_blank')
  }

  const handleNuevoCodigo = async () => {
    if (snapshotRef.current) {
      snapshotRef.current()
      snapshotRef.current = null
    }
    setPaso(PASO.VALIDAR)
    setWaUrl('')
    setExpiresAt(null)
    setTiempoRestante(null)
  }

  // ─── Renders por paso ─────────────────────────────────────────────────────

  if (paso === PASO.FORM) {
    return (
      <div className="min-h-[78vh] flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white/80 border border-black/5 rounded-[2rem] p-8">
          <img src={logo} alt="Maja Club" className="h-14 mx-auto mb-4" />
          <h2 className="text-center font-display text-3xl tracking-tight mb-1">
            Completá tu perfil
          </h2>
          <p className="text-center text-sm text-maja-ink/60 mb-8">
            Estos datos conectan tu cuenta con el Club.
          </p>

          <form onSubmit={handleGuardarPerfil} className="space-y-4">
            {googleUser?.email && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-maja-ink/40 mb-1">
                  Email
                </p>
                <p className="text-sm font-semibold text-maja-ink/70 px-4 py-3 bg-black/5 rounded-2xl">
                  {googleUser.email}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *" type="text" value={form.nombre}
                onChange={updateForm('nombre')} placeholder="Juan" />
              <Campo label="Apellido" type="text" value={form.apellido}
                onChange={updateForm('apellido')} placeholder="Pérez" />
            </div>

            <Campo label="Teléfono celular *" type="tel" value={form.telefono}
              onChange={updateForm('telefono')} placeholder="Ej: 3541 123 456"
              hint="Debe ser tu WhatsApp — lo usamos para verificar tu cuenta." />

            <Campo label="DNI *" type="text" value={form.dni}
              onChange={updateForm('dni')} placeholder="Ej: 35123456" />

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-maja-ink/40 block mb-1">
                Fecha de nacimiento
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div className="relative">
                  <input
                    ref={nacDiaRef}
                    type="number" inputMode="numeric" placeholder="Día"
                    min="1" max="31" value={nacDia}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g,'').slice(0,2)
                      setNacDia(v)
                      setError('')
                      if (v.length === 2) nacMesRef.current?.focus()
                    }}
                    className="w-full h-12 px-3 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div className="relative">
                  <input
                    ref={nacMesRef}
                    type="number" inputMode="numeric" placeholder="Mes"
                    min="1" max="12" value={nacMes}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g,'').slice(0,2)
                      setNacMes(v)
                      setError('')
                      if (v.length === 2) nacAnioRef.current?.focus()
                    }}
                    className="w-full h-12 px-3 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div className="relative">
                  <input
                    ref={nacAnioRef}
                    type="number" inputMode="numeric" placeholder="Año"
                    min="1900" max={new Date().getFullYear()} value={nacAnio}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g,'').slice(0,4)
                      setNacAnio(v)
                      setError('')
                    }}
                    className="w-full h-12 px-3 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
            </div>

            <Campo label="Domicilio" type="text" value={form.domicilio}
              onChange={updateForm('domicilio')} placeholder="Calle y número" />

            {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

            <button
              type="submit"
              disabled={guardando}
              className="w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-60 mt-2"
            >
              {guardando ? 'Guardando...' : 'Continuar →'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  if (paso === PASO.VALIDAR) {
    return (
      <div className="min-h-[78vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white/80 border border-black/5 rounded-[2rem] p-8 text-center">
          <img src={logo} alt="Maja Club" className="h-14 mx-auto mb-4" />
          <div className="text-5xl mb-4">📱</div>
          <h2 className="font-display text-3xl tracking-tight mb-2">
            Verificá tu WhatsApp
          </h2>
          <p className="text-sm text-maja-ink/60 mb-2">
            Necesitamos confirmar que el número de WhatsApp es tuyo.
          </p>
          <p className="text-sm text-maja-ink/70 bg-black/5 rounded-2xl px-4 py-3 mb-6 font-medium">
            📞 {form.telefono}
          </p>

          {error && <p className="text-sm text-red-500 font-medium mb-4">{error}</p>}

          <button
            onClick={handleValidarWhatsApp}
            disabled={generando}
            className="w-full h-14 rounded-full bg-[#25D366] text-white font-bold text-lg disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {generando ? 'Generando código...' : '💬 Validar mi WhatsApp'}
          </button>

          <p className="text-[11px] text-maja-ink/40 mt-4">
            Se abrirá WhatsApp con un mensaje listo para enviar. Solo tocá Enviar.
          </p>
        </div>
      </div>
    )
  }

  if (paso === PASO.ESPERA && cuentaExistente) {
    return (
      <div className="min-h-[78vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white/80 border border-black/5 rounded-[2rem] p-8 text-center">
          <img src={logo} alt="Maja Club" className="h-14 mx-auto mb-4" />
          <div className="text-5xl mb-3">⚠️</div>
          <h2 className="font-display text-2xl tracking-tight mb-2">
            Ya existe una cuenta
          </h2>
          <p className="text-sm text-maja-ink/70 mb-6">
            Ya existe una cuenta de Maja Club asociada a tu número de teléfono o DNI.
            Si es tu cuenta, podés iniciar sesión directamente.
          </p>
          <p className="text-[11px] text-maja-ink/40">
            Si necesitás ayuda para acceder, comunicate con nosotros.
          </p>
        </div>
      </div>
    )
  }

  if (paso === PASO.ESPERA) {
    const expirado = tiempoRestante === 0
    const minutos  = tiempoRestante ? Math.floor(tiempoRestante / 60) : 0
    const segundos = tiempoRestante ? tiempoRestante % 60 : 0

    return (
      <div className="min-h-[78vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white/80 border border-black/5 rounded-[2rem] p-8 text-center">
          <img src={logo} alt="Maja Club" className="h-14 mx-auto mb-4" />

          {!expirado ? (
            <>
              <div className="text-5xl mb-3">⏳</div>
              <h2 className="font-display text-2xl tracking-tight mb-2">
                Esperando confirmación
              </h2>
              <p className="text-sm text-maja-ink/60 mb-4">
                Enviá el mensaje que se abrió en WhatsApp.
                Cuando lo recibamos, tu cuenta quedará verificada automáticamente.
              </p>
              <p className="text-xs text-maja-ink/40 mb-6">
                Código válido por{' '}
                <span className="font-bold text-maja-ink/70">
                  {minutos > 0 ? `${minutos}:${String(segundos).padStart(2, '0')} min` : `${segundos} seg`}
                </span>
              </p>
              <button
                onClick={handleReopenWA}
                className="w-full h-12 rounded-full border border-[#25D366] text-[#25D366] font-semibold mb-3"
              >
                💬 Abrir WhatsApp nuevamente
              </button>
            </>
          ) : (
            <>
              <div className="text-5xl mb-3">⌛</div>
              <h2 className="font-display text-2xl tracking-tight mb-2">
                El código expiró
              </h2>
              <p className="text-sm text-maja-ink/60 mb-6">
                Generá un nuevo código para continuar.
              </p>
            </>
          )}

          <button
            onClick={handleNuevoCodigo}
            className="w-full h-12 rounded-full bg-maja-red text-white font-semibold"
          >
            {expirado ? 'Generar nuevo código' : 'Usar otro código'}
          </button>
        </div>
      </div>
    )
  }

  return null
}

// Campo de formulario reutilizable
const Campo = ({ label, hint, ...inputProps }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-maja-ink/40 block mb-1">
      {label}
    </label>
    <input
      {...inputProps}
      className="w-full h-12 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red text-sm"
    />
    {hint && <p className="text-[11px] text-maja-ink/40 mt-1 px-1">{hint}</p>}
  </div>
)

export default OnboardingFlow
